import { useFeatureFlags, useSubscriptionDetails } from '@features/user';
import { useUserMetrics } from '@features/user/store/hooks';

import { useOnboardingTasks } from '../components/OnboardingTasksCard/hooks/useOnboardingTasks';
import type { DashboardState } from '../config/types';

import { useFirstNoteStatus } from './useFirstNoteStatus';
import { useHasRealPatients } from './useHasRealPatients';
import { useUpcomingRelevantAppointments } from './useUpcomingRelevantAppointments';

/**
 * Aggregates all dashboard-related state into a single object.
 * This is the single source of truth for dashboard card visibility logic.
 */
export const useDashboardState = (): DashboardState => {
  // Fetch all necessary state from various hooks
  const { hasCreatedFirstNote, isLoading: notesLoading } = useFirstNoteStatus();
  const { showOnboarding, isLoading: onboardingLoading } = useOnboardingTasks();
  const { hasRealPatients, isLoading: patientsLoading } = useHasRealPatients();
  const userMetrics = useUserMetrics();
  const featureFlags = useFeatureFlags();

  // Use shared hook to determine if we should show the appointments card
  const { hasRelevantAppointments: hasUpcomingAppointments, isLoading: appointmentsLoading } = useUpcomingRelevantAppointments();

  // Get subscription status for determining referral card visibility
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const isActiveSubscriber = subscriptionDetails?.status === 'active';

  // Aggregate loading states
  const isLoading = notesLoading || onboardingLoading || patientsLoading || appointmentsLoading;

  return {
    hasCreatedFirstNote,
    showOnboarding: showOnboarding ?? false,
    hasRealPatients,
    hasUpcomingAppointments,
    userMetrics: userMetrics ?? null,
    featureFlags: featureFlags ?? null,
    isLoading,
    isActiveSubscriber,
  };
};